import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { getSession, isAdminSession } from '../api/session';
import { AppShell } from '../components/AppShell';

// Route-level code splitting: each page is its own chunk, so heavy deps
// (e.g. recharts on Dashboard) stay off the initial load.
const HomePage = lazy(() => import('../pages/HomePage').then((m) => ({ default: m.HomePage })));
const CaseConfigPage = lazy(() =>
  import('../pages/CaseConfigPage').then((m) => ({ default: m.CaseConfigPage }))
);
const DashboardPage = lazy(() =>
  import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const AssessmentPage = lazy(() =>
  import('../pages/AssessmentPage').then((m) => ({ default: m.AssessmentPage }))
);
const ScoringPage = lazy(() =>
  import('../pages/ScoringPage').then((m) => ({ default: m.ScoringPage }))
);
const ReportPage = lazy(() => import('../pages/ReportPage').then((m) => ({ default: m.ReportPage })));
const ConnectedPersonasPage = lazy(() =>
  import('../pages/ConnectedPersonasPage').then((m) => ({ default: m.ConnectedPersonasPage }))
);
const AdminPage = lazy(() => import('../pages/AdminPage').then((m) => ({ default: m.AdminPage })));

function RouteFallback() {
  const { t } = useTranslation();
  return <div className="empty-state">{t('common.loading')}</div>;
}

function AdminRoute() {
  return isAdminSession(getSession()) ? (
    <Suspense fallback={<RouteFallback />}>
      <AdminPage />
    </Suspense>
  ) : (
    <Navigate to="/" replace />
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route
            index
            element={
              <Suspense fallback={<RouteFallback />}>
                <HomePage />
              </Suspense>
            }
          />
          <Route
            path="case/:caseId/config"
            element={
              <Suspense fallback={<RouteFallback />}>
                <CaseConfigPage />
              </Suspense>
            }
          />
          <Route
            path="case/:caseId/dashboard"
            element={
              <Suspense fallback={<RouteFallback />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route
            path="case/:caseId/assessment"
            element={
              <Suspense fallback={<RouteFallback />}>
                <AssessmentPage />
              </Suspense>
            }
          />
          <Route
            path="case/:caseId/scoring"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ScoringPage />
              </Suspense>
            }
          />
          <Route
            path="case/:caseId/report"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ReportPage />
              </Suspense>
            }
          />
          <Route
            path="case/:caseId/connected"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ConnectedPersonasPage />
              </Suspense>
            }
          />
          <Route
            path="admin"
            element={<AdminRoute />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
